import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import styled from '@emotion/styled';
// https://github.com/import-js/eslint-plugin-import/issues/2266
// eslint-disable-next-line import/no-unresolved
import { A11y, Navigation } from 'swiper/modules';
// eslint-disable-next-line import/no-unresolved
import { Swiper, SwiperSlide } from 'swiper/react';

import useUserContext from '../../hooks/use-user-context';
import useSwiperRef from '../../hooks/use-swiper-ref';
import { createOptionMap } from '../../utilities/misc';
import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { media } from '../../theme/media';
import { headers } from '../../styles/type';
import { containerStyles } from '../../styles/layout';

import ButtonArrow from '../buttons/ButtonArrow';
import ButtonText from '../buttons/ButtonText';
import TextCard from '../TextCard';
import TextRenderer from '../TextRenderer';

// eslint-disable-next-line import/no-unresolved
import 'swiper/css';

const Container = styled.div`
  background-color: ${({ backgroundColor }) => (backgroundColor === 'Blue' ? colors.blue['50'] : colors.white)};
  padding: ${rem(16)} 0 ${rem(40)};
  overflow: hidden;

  ${media('>tiny')} {
    padding: ${rem(40)} 0 ${rem(56)};
  }
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
`;

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: ${rem(40)};
`;

const Headline = styled.h2`
  ${headers.h3}
  padding-right: ${rem(24)};
  margin-top: ${rem(16)};
  margin-right: auto;
  margin-bottom: ${rem(16)};
  color: ${colors.blue['800']};
`;

const SwiperStyled = styled(Swiper)`
  overflow: visible;

  .swiper-slide {
    ${media('>mini')} {
      --count: 2;
      --gap: ${rem(40)};
      margin-right: var(--gap);
      width: calc((100% / var(--count)) - var(--gap) + (var(--gap) / var(--count)));
    }

    ${media('>smedium')} {
      --count: 3;
    }
  }
`;

const SliderArrows = styled.div`
  display: flex;
  margin: ${rem(-8)};
  padding-top: ${rem(32)};

  ${({ visible }) => (!visible && 'display: none')};

  ${ButtonArrow} {
    margin: ${rem(8)};
  }
`;

const CardSlider = ({ headline, ctaText, ctaLink, cardItems, backgroundColor }) => {
  const data = useStaticQuery(graphql`
    query CardSlider {
      allContentfulOptionSet(filter: {name: {eq: "slider"}}) {
        nodes {
          ...OptionSetFragment
        }
      }
    }
  `);
  const { language } = useUserContext();
  const options = createOptionMap(data.allContentfulOptionSet.nodes, language);
  const [prevButton, prevButtonRef] = useSwiperRef();
  const [nextButton, nextButtonRef] = useSwiperRef();
  const [arrowsVisible, setArrowsVisible] = useState(false);

  return (
    <Container
      backgroundColor={backgroundColor}
    >
      <ContentWrapper>
        <Header>
          {headline && (
            <Headline>
              <TextRenderer text={headline} />
            </Headline>
          )}
          {ctaText && ctaLink && (
            <ButtonText to={ctaLink} arrow>
              <TextRenderer text={ctaText} />
            </ButtonText>
          )}
        </Header>
        <SwiperStyled
          modules={[Navigation, A11y]}
          slidesPerView={1}
          spaceBetween={40}
          threshold={20}
          navigation={{
            prevEl: prevButton,
            nextEl: nextButton
          }}
          breakpoints={{
            640: {
              slidesPerView: 2
            },
            1024: {
              slidesPerView: 3
            }
          }}
          onBreakpoint={(_swiper, params) => {
            setArrowsVisible(cardItems.length > params.slidesPerView);
          }}
        >
          {cardItems && cardItems.map((cardItem, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <SwiperSlide style={{ height: 'auto' }} key={index}>
              <TextCard
                icon={cardItem.icon}
                subhead={cardItem.subhead}
                headline={cardItem.headline}
                text={cardItem.text}
                primaryCtaText={cardItem.primaryCtaText}
                primaryCtaLink={cardItem.primaryCtaLink}
                secondaryCtaText={cardItem.secondaryCtaText}
                secondaryCtaLink={cardItem.secondaryCtaLink}
                featured={cardItem.featured}
              />
            </SwiperSlide>
          ))}
          <SliderArrows visible={arrowsVisible}>
            <ButtonArrow
              ref={prevButtonRef}
              aria-label={options['slider.prev_button_label']}
              left
            />
            <ButtonArrow
              ref={nextButtonRef}
              aria-label={options['slider.next_button_label']}
            />
          </SliderArrows>
        </SwiperStyled>
      </ContentWrapper>
    </Container>
  );
};

CardSlider.propTypes = {
  headline: PropTypes.string,
  ctaText: PropTypes.string,
  ctaLink: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  cardItems: PropTypes.arrayOf(
    PropTypes.object
  )
};

export const query = graphql`
  fragment CardSliderFragment on ContentfulCardSlider {
    id
    __typename
    headline
    ctaText
    ctaLink
    cardItems {
      id
      icon {
        gatsbyImageData(width: 80, height: 80, quality: 75)
        ...ImageAssetFragment
      }
      subhead
      headline
      text {
        raw
      }
      primaryCtaText
      primaryCtaLink
      secondaryCtaText
      secondaryCtaLink
      featured
    }
    backgroundColor
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default CardSlider;
